// ---------------------------------------------------------
// Datei: AuthContext.tsx
// Zweck: Globaler Authentifizierungs-Context inkl. Login/Register Flow
// Besonderheiten:
// - Persistiert JWT im localStorage
// - Lädt Profil beim App-Start und schützt Routen
// - Räumt Sync-State beim Logout auf
// Stand: 28.10.2025
// ---------------------------------------------------------
import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as loginApi, register as registerApi } from '../services/api';
import { clearSyncState } from '../services/queueService';
import { AuthUser } from '../types/auth';

// Definiere den Context-Typ
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

// Erstelle den Kontext mit Standardwerten
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  register: async () => {},
  loginWithGoogle: async () => {},
  loginWithToken: async () => {},
  logout: async () => {},
  error: null
});

import { getEnv } from "../utils/env";

// Dev: Vite-Proxy (/auth-api → localhost:7000), Prod: direkt zur Auth-API (CORS konfiguriert)
const AUTH_PROXY = import.meta.env.DEV ? '/auth-api' : (getEnv('VITE_AUTH_API_URL') || '/auth-api');
const PROFILE_CACHE_KEY = 'google_profile';
const DEV_TOKEN_KEY = 'dev_access_token';

function loadCachedProfile(): AuthUser | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function saveProfileCache(user: AuthUser): void {
  localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(user));
}

function clearProfileCache(): void {
  localStorage.removeItem(PROFILE_CACHE_KEY);
}

async function fetchGoogleMe(): Promise<AuthUser> {
  const res = await fetch(`${AUTH_PROXY}/google/me`, { credentials: 'include' });
  if (!res.ok) throw new Error('Profil konnte nicht geladen werden');
  const data = await res.json();
  const p = data.profile;
  const user: AuthUser = {
    id: p.id,
    name: p.name,
    email: p.email,
    picture: p.picture,
  };
  saveProfileCache(user);
  return user;
}

async function fetchGoogleMeWithToken(token: string): Promise<AuthUser> {
  const res = await fetch(`${AUTH_PROXY}/google/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Token ungültig oder abgelaufen');
  const data = await res.json();
  const p = data.profile;
  const user: AuthUser = {
    id: p.id,
    name: p.name,
    email: p.email,
    picture: p.picture,
  };
  saveProfileCache(user);
  return user;
}

// Provider-Komponente
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Beim Laden prüfen, ob der Benutzer bereits angemeldet ist
  useEffect(() => {
    const checkAuth = async () => {
      // 1. Gecachtes Profil sofort laden (verhindert Ladeflackern)
      const cached = loadCachedProfile();
      if (cached) {
        setUser(cached);
        setIsLoading(false);
      }

      // 2. Dev-Token prüfen (überschreibt Google-Session)
      const devToken = localStorage.getItem(DEV_TOKEN_KEY) || getEnv('VITE_DEV_ACCESS_TOKEN') || null;
      if (devToken) {
        try {
          const userData = await fetchGoogleMeWithToken(devToken);
          setUser(userData);
        } catch {
          localStorage.removeItem(DEV_TOKEN_KEY);
          clearProfileCache();
          setUser(null);
        }
        setIsLoading(false);
        return;
      }

      // 3. Session via /google/authenticated prüfen (immer, auch bei Cache)
      try {
        const authRes = await fetch(`${AUTH_PROXY}/google/authenticated`, { credentials: 'include' });
        if (authRes.ok) {
          const authData = await authRes.json();
          if (authData?.status) {
            if (!cached) {
              // Kein Cache – Profil nachladen
              const userData = await fetchGoogleMe();
              setUser(userData);
            }
            setIsLoading(false);
            return;
          }
          // Server sagt explizit status: false → ausloggen
          clearProfileCache();
          setUser(null);
        } else if (authRes.status === 401) {
          // Explizit nicht autorisiert → ausloggen
          clearProfileCache();
          setUser(null);
        } else {
          // 5xx, 502 etc. – API nicht erreichbar, gecachten Zustand behalten
          if (!cached) setIsLoading(false);
          return;
        }
      } catch {
        // Netzwerkfehler – gecachten Zustand behalten
        if (!cached) setIsLoading(false);
        return;
      }

      setIsLoading(false);
    };

    checkAuth();

    const handleUnauthorized = () => {
      clearProfileCache();
      localStorage.removeItem(DEV_TOKEN_KEY);
      setUser(null);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  // Login-Funktion
  const login = async (email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await loginApi(email, password);
      setUser(response.user);
      navigate('/projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Anmeldung fehlgeschlagen';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Registrierungs-Funktion
  const register = async (name: string, email: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await registerApi(name, email, password);
      setUser(response.user);
      navigate('/projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registrierung fehlgeschlagen';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Google Login-Funktion – authentifiziert via Session-Cookie der Auth-API
  const loginWithGoogle = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const userData = await fetchGoogleMe();
      setUser(userData);
      navigate('/projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Google Login fehlgeschlagen';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Developer Login – authentifiziert via Access Token als Bearer
  const loginWithToken = async (token: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      const userData = await fetchGoogleMeWithToken(token);
      localStorage.setItem(DEV_TOKEN_KEY, token);
      setUser(userData);
      navigate('/projects');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Token-Login fehlgeschlagen';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout-Funktion mit Sync-State Cleanup
  const logout = async (): Promise<void> => {
    localStorage.removeItem(DEV_TOKEN_KEY);
    try {
      await fetch(`${AUTH_PROXY}/google/logout`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch {
      // Session-Löschung fehlgeschlagen, trotzdem lokal ausloggen
    }
    clearProfileCache();
    clearSyncState();
    setUser(null);
    navigate('/login');
  };

  // Kontext-Wert
  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    loginWithGoogle,
    loginWithToken,
    logout,
    error
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook für einfachen Kontext-Zugriff
export const useAuth = () => useContext(AuthContext);
